import React, {useState} from 'react';
import {Alert, Box, Button, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import useNotification from '../../../../../utility/hooks/useNotification';
import {useDispenseSchoolStockMutation, useGetSchoolStockOptionsQuery} from '../../../../../api/services/medicinesService';
import {useGetMedicalOtcQuery} from '../../../../../api/services/studentService';
import {useGetMedicalConsentOptionsQuery} from '../../../../../api/services/lookupsService';
import {countInUnit} from '../../../../../utility/medicines';
import useUser from '../../../../../utility/hooks/useUser';

const nowForInput = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    return new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
};

const formatGivenAt = (value) => value
    ? new Date(value).toLocaleString('en-GB', {day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London'})
    : '';

const emptyDose = {medicineLookupId: '', dosesGiven: '', givenAt: '', note: ''};

// AD-2803: the warning staff see before saving a dose of school stock the parents have not consented to.
// The OTC item linked to the medicine names it; a medicine with no linked item is named itself.
export const consentWarning = (option, consentedIds, otcItems, studentName) => {
    if (!option) return '';
    if (option.medicalConsentLookupId && (consentedIds ?? []).includes(option.medicalConsentLookupId)) return '';
    const item = (otcItems ?? []).find(o => o.id === option.medicalConsentLookupId);
    return `${studentName}'s parents have not given consent for ${item?.description ?? option.description} under Non-Prescription (OTC) Medication.`;
};

// AD-2803: doses given to a student from the school's own stock, on the student's Medical tab. Everyone who can see
// Medicine at School sees them; staff who can edit it record one.
const SchoolStockDoses = ({studentId, studentName, canRecord, dispenses, units, onRecorded}) => {
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {user} = useUser();
    // Pike Fold, 3 November 2026: a school can hide Non-Prescription (OTC) Medication from its staff. The server
    // refuses the child's OTC consents to anyone it is hidden from, and a refused request sends them back to the
    // Profiles page, so the consents are only asked for, and the warning only shown, when the section can be seen.
    const canSeeOtc = ['View', 'Edit'].some(level => (user?.permissions ?? []).includes(`Permission.StudentMedicalConsents.${level}`));
    const {data: options = [], refetch: refetchOptions} = useGetSchoolStockOptionsQuery(undefined, {skip: !canRecord});
    const {data: otc} = useGetMedicalOtcQuery(studentId, {skip: !canRecord || !canSeeOtc});
    const {data: otcList} = useGetMedicalConsentOptionsQuery(undefined, {skip: !canRecord || !canSeeOtc});
    const [dispense] = useDispenseSchoolStockMutation();
    const [dose, setDose] = useState(null);
    const doses = (dispenses ?? [])
        .filter(d => d.fromSchoolStock)
        .sort((a, b) => new Date(b.givenAt) - new Date(a.givenAt));

    const option = options.find(o => o.medicineLookupId === Number(dose?.medicineLookupId));
    const consentedIds = (otc?.medicalConsentOptions?.options ?? []).map(o => o.optionId);
    const warning = canSeeOtc ? consentWarning(option, consentedIds, otcList?.options, studentName) : '';
    const amount = Number(dose?.dosesGiven);
    const amountOk = Number.isInteger(amount) && amount >= 1 && (!option || amount <= option.dosesLeft);

    const handleSave = async () => {
        try {
            await dispense({
                studentId,
                medicineLookupId: option.medicineLookupId,
                dosesGiven: amount,
                note: dose.note,
                givenAt: dose.givenAt ? new Date(dose.givenAt).toISOString() : null
            }).unwrap();
            showSuccessNotification('Dose recorded');
            setDose(null);
            await refetchOptions();
            await onRecorded();
        } catch (error) {
            showErrorNotification(error?.data?.validationErrors?.[0]?.errorMessage ?? 'The dose could not be recorded');
        }
    };

    if (doses.length === 0 && !canRecord) return null;

    return (
        <Box sx={{mt: 2}} data-testid="school-stock-doses">
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>Doses from school stock</Typography>
            {doses.length > 0 && (
                    <Table size="small" sx={{mb: 1}}>
                        <TableHead>
                            <TableRow sx={{'& th': {backgroundColor: '#f1faf7', fontWeight: 600}}}>
                                <TableCell>Given</TableCell>
                                <TableCell>Medicine</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Given by</TableCell>
                                <TableCell>Note</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {doses.map(d => (
                                <TableRow key={d.id} data-testid={`school-stock-dose-${d.id}`}>
                                    <TableCell>{formatGivenAt(d.givenAt)}</TableCell>
                                    <TableCell>{d.medicine}</TableCell>
                                    <TableCell>{countInUnit(d.dosesGiven, d.dosageUnit, units)}</TableCell>
                                    <TableCell>{d.givenBy}</TableCell>
                                    <TableCell>{d.note}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
            )}
            {canRecord && (
                <Button
                    variant="outlined"
                    data-testid="school-stock-dose-open"
                    disabled={options.length === 0}
                    onClick={() => setDose({...emptyDose, givenAt: nowForInput()})}
                >
                    Give from school stock
                </Button>
            )}

            <AdmicityDialog
                open={Boolean(dose)}
                title="Give from school stock"
                handleClose={() => setDose(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setDose(null)},
                    {label: 'Save', onClick: handleSave, disabled: !option || !amountOk || !dose?.givenAt || dose.givenAt > nowForInput()}
                ]}
            >
                {dose && (
                    <Stack gap={2} sx={{mt: 1}}>
                        <TextField
                            select
                            fullWidth
                            label="Medicine"
                            data-testid="school-stock-dose-medicine"
                            value={dose.medicineLookupId}
                            onChange={event => setDose({...dose, medicineLookupId: event.target.value})}
                        >
                            {options.map(o => (
                                <MenuItem key={o.medicineLookupId} value={o.medicineLookupId}>
                                    {o.description} ({countInUnit(o.dosesLeft, o.dosageUnit, units)} in stock)
                                </MenuItem>
                            ))}
                        </TextField>
                        {warning && <Alert severity="warning" data-testid="school-stock-dose-warning">{warning}</Alert>}
                        <TextField
                            label="Date and time given"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{shrink: true}}
                            inputProps={{max: nowForInput(), 'data-testid': 'school-stock-dose-given-at'}}
                            value={dose.givenAt}
                            onChange={event => setDose({...dose, givenAt: event.target.value})}
                        />
                        <TextField
                            label={option ? `Amount given (${countInUnit(2, option.dosageUnit, units).replace(/^2 /, '')})` : 'Amount given'}
                            type="number"
                            fullWidth
                            data-testid="school-stock-dose-amount"
                            inputProps={{min: 1, step: 1}}
                            error={dose.dosesGiven !== '' && !amountOk}
                            helperText={dose.dosesGiven !== '' && !amountOk && option
                                ? `Enter a whole number up to ${option.dosesLeft}, the amount in the school stock`
                                : ''}
                            value={dose.dosesGiven}
                            onChange={event => setDose({...dose, dosesGiven: event.target.value})}
                        />
                        <TextField
                            label="Note"
                            fullWidth
                            multiline
                            minRows={2}
                            data-testid="school-stock-dose-note"
                            inputProps={{maxLength: 1000}}
                            value={dose.note}
                            onChange={event => setDose({...dose, note: event.target.value})}
                        />
                    </Stack>
                )}
            </AdmicityDialog>
        </Box>
    );
};

SchoolStockDoses.propTypes = {
    studentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    studentName: PropTypes.string,
    canRecord: PropTypes.bool,
    dispenses: PropTypes.array,
    units: PropTypes.array,
    onRecorded: PropTypes.func.isRequired
};

export default SchoolStockDoses;
